import { useState, useEffect } from "react";
import Select from "@/components/molecules/Select";
import Button from "@/components/atoms/Button";
import Ticket from "@/types/Ticket";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import qaAssigneeSchema from "@/schemas/qaAssignee";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import CKEditorWithFileUpload from "@/components/atoms/CKEditor";
import FileUpload from "@/components/atoms/FileUpload";
import { Pencil } from "lucide-react";
import useAccess from "@/hooks/useAccess";
import { router } from "@inertiajs/react";
import axios from 'axios';

type Props = {
	ticket: Ticket;
	setSelectedTicketStatus: (status: string) => void;
	qualityTickets: any[];
	users: any[];
};

const QualityAssigneeForm = ({ ticket, setSelectedTicketStatus, qualityTickets, users }: Props) => {
	const [lastQaTicket, setLastQaTicket] = useState<any>(qualityTickets.filter((qa) => qa.passed === null && qa.issue === null).pop() || null);
	const [editorData, setEditorData] = useState("");
	const [fileUploadKey, setFileUploadKey] = useState(0);
	const [edit, setEdit] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const { currentUser } = useAccess();

	const handleEditorChange = (data: string) => {
    setEditorData(data);
    form.setValue('qa_comment_for_assignee', data);
  };

	const form = useForm({
    resolver: zodResolver(qaAssigneeSchema),
    defaultValues: {
			qa_assigneee_id: "",
			qa_comment_for_assignee: "",
			files: [],
		}
  });

	useEffect(() => {
    if (lastQaTicket) {
			form.reset({
				qa_assigneee_id: lastQaTicket.assignee.id.toString(),
				qa_comment_for_assignee: lastQaTicket.comment_for_assignee,
				files: [],
      });

			setEditorData(lastQaTicket?.comment_for_assignee || "");
		}
      
  }, [lastQaTicket]);

	const handleSubmit = async (data: any) => {
		setIsSubmitting(true);

    const payload = {
      ticket: {
        qa_assigneee_id: data.qa_assigneee_id,
        qa_comment_for_assignee: data.qa_comment_for_assignee
      },
      files: data.files.map((file: any) => ({
        filename: file.name,
        filesize: file.size,
        filetype: file.content_type,
        filepath: file.path,
        original_name: file.original_name,
        fileurl: file.url,
      })),
    };

		try {
			router.patch(
				`/tickets/${ticket.id}/${edit ? 'update_qa_assignee' : 'qa_assignee'}`,
				payload,
				{
					onSuccess: (page) => {
						setFileUploadKey((prevKey) => prevKey + 1);
						setEditorData("");
						setEdit(false);
					},
					onError: (errors) => {
						setIsSubmitting(false);
					},
					onFinish: () => {
						setIsSubmitting(false);
					},
				}
			);
		} catch (error) {
			setIsSubmitting(false);
		}
	}

	useEffect(() => {
		const newLastQaTicket =
		qualityTickets.filter((qa) => qa.passed === null && qa.issue === null).pop() || null;
		setLastQaTicket(newLastQaTicket);
		setSelectedTicketStatus(ticket.status.id.toString());
	}, [qualityTickets]);

	const formatDate = (input: string): string => {
    const date = new Date(input.replace(/(\d+)\/(\d+)\/(\d+)/, '$2/$1/$3'));
    
    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };
  
    return date.toLocaleDateString('en-GB', options).replace(',', '');
  };

	return (
		<div>
			{!edit && lastQaTicket ? (
				<>
					<div>
						<div className="flex space-x-4">
							<span className="mt-1">QA Assigned to:</span>
							{currentUser && currentUser.id !== lastQaTicket.assignee.id && (
								<div className="flex items-center space-x-2 mr-2 ml-4">
									<Pencil
										size={16}
										className="cursor-pointer text-gray-500 hover:text-gray-800"
										onClick={() => setEdit(true)}
									/>
								</div>
							)}
							<div className="flex space-x-6 mt-1">
								<span className="text-xs mt-1">{formatDate(lastQaTicket?.created_at)}</span>
								<p className="font-semibold">{lastQaTicket.assignee.name}</p>
							</div>

							<img
								src={"avatar_url" in lastQaTicket.assignee ? (lastQaTicket.assignee.avatar_url) : "/default-avatar.png"}
								alt="Profile"
								className="w-8 h-8 rounded-full dark:bg-darkTextColor"
							/>
						</div>
					</div>
				</>
				
			): (
				<Form {...form}>
				<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
					
					<FormField
						control={form.control}
						name="qa_assigneee_id"
						render={({ field }) => (
							<FormItem className="w-1/5">
								<FormLabel>Choose Staff*</FormLabel>
								<FormControl>
									<Select
										options={users.map((user) => ({
											label: user.name,
											value: user.id.toString(),
										}))}
										value={field.value?.toString()}
										onChange={(value) => field.onChange(value)}
										enabledSearch={true}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
			
					<FormField
						control={form.control}
						name="qa_comment_for_assignee"
						render={({ field }) => (
							<FormItem className="w-full">
								<FormLabel></FormLabel>
								<FormControl>
									<CKEditorWithFileUpload
										value={editorData}
										onChange={handleEditorChange}
										isPublic={true}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="files"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Attachments</FormLabel>
								<FormControl>
									<FileUpload
										key={fileUploadKey}
										files={field.value || []}
										onChange={(files) => field.onChange(files)}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
						
					<div className="flex gap-2">
						{edit && (
							<Button type="button" variant="outline" onClick={() => setEdit(false)}>
								Cancel
							</Button>
						)}
						<Button type="submit" disabled={(edit && isSubmitting)}>
							Save
						</Button>
					</div>
				</form>
				</Form>
			)}
			
		</div>
    
  );
};

export default QualityAssigneeForm;
